/**
 * V2 achievement presentation mapping.
 * Backend IDs, names, unlock logic, and V1 ImageIcon assets stay untouched.
 */

export function achievementFamily(item, tabHint) {
    const typeId = Number(item?.achievementTypeId);
    if (typeId === 2 || tabHint === 'milestones') return 'milestone';
    if (typeId === 3 || tabHint === 'streaks') return 'streak';
    if (typeId === 1 || tabHint === 'badges') return 'badge';

    const id = Number(item?.id);
    if (id >= 62) return 'streak';
    if (id >= 13) return 'milestone';
    return 'badge';
}

export function achievementNumericValue(item, fallback = 0) {
    const raw = item?.achievementValue ?? fallback;
    const n = Number(String(raw).replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : 0;
}

export function achievementMotif(item, family) {
    const id = Number(item?.id);
    const name = String(item?.achievementName || '').toLowerCase();
    const file = String(item?.fileName || '').toLowerCase();

    if (id === 1 || name.includes('blueprint') || file.includes('blueprint')) {
        return 'blueprint';
    }
    if (id === 2 || name.includes('breakthrough') || file.includes('breakthrough')) {
        return 'breakthrough';
    }
    if (id === 3 || name.includes('path') || file.includes('path')) {
        return 'path';
    }
    if (id === 8 || name.includes('goal') || file.includes('goal')) {
        return 'goal';
    }
    if (id === 9 || name.includes('mystery')) return 'mystery';
    if (id === 10 || name === 'level 1') return 'level1';
    if (id === 11 || name === 'level 2') return 'level2';
    if (id === 12 || name === 'level 3') return 'level3';
    if (id === 4 || (family === 'badge' && name.includes('milestone 77'))) {
        return 'special77';
    }
    if (id === 6 || name.includes('777')) return 'special777';
    if (id === 5 || (family === 'badge' && name.includes('77 day'))) {
        return 'streak77';
    }
    if (id === 7 || name.includes('365')) return 'streak365';
    if (family === 'streak') return 'streak';
    if (family === 'milestone') return 'milestone';
    return 'badge';
}

/** 0 first step → 4 legendary. Drives ring complexity / gold weight. */
export function achievementTier(family, motif, value) {
    if (motif === 'special777' || motif === 'streak365') return 4;
    if (motif === 'special77' || motif === 'streak77') return 3;
    if (motif === 'level3') return 3;
    if (motif === 'level2') return 2;
    if (motif === 'blueprint' || motif === 'breakthrough' || motif === 'path') {
        return 2;
    }
    if (family === 'milestone') {
        if (value >= 1000) return 4;
        if (value >= 500) return 3;
        if (value >= 100) return 2;
        if (value >= 30) return 1;
        return 0;
    }
    if (family === 'streak') {
        if (value >= 300) return 4;
        if (value >= 100) return 3;
        if (value >= 30) return 2;
        if (value >= 7) return 1;
        return 0;
    }
    return 1;
}

export function formatAchievementValue(value) {
    if (!Number.isFinite(value) || value <= 0) return '';
    return value.toLocaleString('en-US');
}

export function achievementRequirement(item, family) {
    const value = achievementNumericValue(item);
    const id = Number(item?.id);
    const name = String(item?.achievementName || '').trim();

    if (family === 'streak') {
        if (value === 1) return 'Complete your first daily streak';
        return `Reach a ${value}-day streak`;
    }
    if (family === 'milestone') {
        if (value === 1) return 'Take your first action step';
        return `Complete ${formatAchievementValue(value)} action steps`;
    }

    switch (id) {
        case 1:
            return 'Complete the Design My Life Blueprint';
        case 2:
            return 'Complete My Breakthrough';
        case 3:
            return 'Complete My Path';
        case 4:
            return 'Complete 77 action steps';
        case 5:
            return 'Reach a 77-day streak';
        case 6:
            return 'Complete 777 action steps';
        case 7:
            return 'Reach a 365-day streak';
        case 8:
            return 'Complete a goal';
        case 9:
            return 'Keep going — this one reveals itself';
        case 10:
            return 'Graduate from Level 1 training';
        case 11:
            return 'Graduate from Level 2 training';
        case 12:
            return 'Graduate from Level 3 training';
        default:
            return name ? `Unlock ${name}` : 'Keep showing up to unlock this';
    }
}

export function achievementDisplayTitle(item) {
    return (
        item?.socialMediaTitle ||
        item?.achievementName ||
        'Achievement'
    );
}

export function parseAchievementDate(value, compact = false) {
    if (!value) return '';
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) {
        return d.toLocaleDateString(undefined, {
            year: 'numeric',
            month: compact ? 'short' : 'long',
            day: 'numeric',
        });
    }
    return String(value);
}

export function recentlyEarned(groups, limit = 3) {
    const all = [
        ...(groups.badges || []),
        ...(groups.milestones || []),
        ...(groups.streaks || []),
    ].filter((item) => item && item.achieved);
    all.sort((a, b) => {
        const da = new Date(a.completionDate || 0).getTime();
        const db = new Date(b.completionDate || 0).getTime();
        return (Number.isNaN(db) ? 0 : db) - (Number.isNaN(da) ? 0 : da);
    });
    return all.slice(0, limit);
}

/**
 * Next unearned milestone/streak from catalog + existing progress counts.
 * Returns null if progress values are missing or nothing is derivable.
 */
export function nextJourneyAchievement(groups, progress) {
    const steps = Number(progress?.stepsTaken);
    const streak = Number(progress?.currentStreak);
    const hasSteps = Number.isFinite(steps);
    const hasStreak = Number.isFinite(streak);

    if (hasSteps) {
        const locked = (groups?.milestones || [])
            .filter((item) => item && !item.achieved)
            .map((item) => ({
                item,
                target: achievementNumericValue(item),
            }))
            .filter((row) => row.target > 0)
            .sort((a, b) => a.target - b.target);
        const next =
            locked.find((row) => row.target > steps) || locked[0] || null;
        if (next) {
            const remaining = Math.max(0, next.target - steps);
            return {
                item: next.item,
                family: 'milestone',
                current: steps,
                target: next.target,
                remaining,
                label:
                    next.target === 1
                        ? '1 Action Step'
                        : `${formatAchievementValue(next.target)} Action Steps`,
                unit: remaining === 1 ? 'step' : 'steps',
            };
        }
    }

    if (hasStreak) {
        const locked = (groups?.streaks || [])
            .filter((item) => item && !item.achieved)
            .map((item) => ({
                item,
                target: achievementNumericValue(item),
            }))
            .filter((row) => row.target > 0)
            .sort((a, b) => a.target - b.target);
        const next =
            locked.find((row) => row.target > streak) || locked[0] || null;
        if (next) {
            const remaining = Math.max(0, next.target - streak);
            return {
                item: next.item,
                family: 'streak',
                current: streak,
                target: next.target,
                remaining,
                label:
                    next.target === 1
                        ? '1 Day'
                        : `${formatAchievementValue(next.target)} Days`,
                unit: remaining === 1 ? 'day' : 'days',
            };
        }
    }

    return null;
}
